import React, { useState } from 'react';
import { DesktopDatePicker, DesktopDatePickerProps } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';

export interface DatePickerProps extends Partial<DesktopDatePickerProps<string | undefined, unknown>> {
  value: string | undefined;
  onChange: (value: unknown, keyboardInputValue?: string | undefined) => void;
  onOpen: () => void;
  inputClassName?: string;
}

/** Simple calendar icon for the picker button (inline SVG, no asset dependency) */
function CalendarIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.75 1a.75.75 0 00-1.5 0v1.273c-.187.008-.364.018-.53.03-.694.056-1.282.171-1.82.437A4.75 4.75 0 002.74 4.9c-.266.538-.38 1.126-.436 1.82-.054.679-.054 1.52-.054 2.581V14.633c0 1.092 0 1.958.057 2.655.058.714.18 1.317.46 1.869a4.75 4.75 0 002.077 2.075c.55.281 1.154.403 1.868.461.697.057 1.563.057 2.655.057H14.633c1.092 0 1.958 0 2.655-.057.714-.058 1.317-.18 1.869-.46a4.75 4.75 0 002.075-2.076c.281-.552.403-1.155.461-1.869.057-.697.057-1.563.057-2.655V9.367c0-1.092 0-1.958-.057-2.655-.058-.714-.18-1.317-.46-1.868a4.75 4.75 0 00-2.076-2.076c-.552-.281-1.155-.403-1.869-.461-.169-.014-.348-.024-.538-.032V1a.75.75 0 00-1.5 0v1.25H8.75V1zm6.5 3v-.25H8.75V4a.75.75 0 01-1.5 0v-.226c-.146.007-.282.015-.411.025-.604.048-.979.14-1.276.286a3.25 3.25 0 00-1.478 1.478c-.147.297-.238.672-.286 1.276-.048.612-.049 1.394-.049 2.494V14.6c0 1.133 0 1.937.052 2.566.05.62.147 1.005.302 1.31a3.25 3.25 0 001.42 1.42c.305.155.69.251 1.31.302.63.051 1.434.052 2.566.052h5.2c1.133 0 1.937 0 2.566-.052.62-.05 1.005-.147 1.31-.302a3.25 3.25 0 001.42-1.42c.155-.305.251-.69.302-1.31.051-.63.052-1.434.052-2.566V9.4c0-1.132 0-1.937-.052-2.566-.05-.62-.147-1.005-.302-1.31a3.25 3.25 0 00-1.42-1.42c-.305-.155-.69-.251-1.31-.302-.13-.01-.269-.02-.416-.026V4a.75.75 0 01-1.5 0zM7 7.25a.75.75 0 000 1.5h10a.75.75 0 000-1.5H7z"
        fill="#101828"
      />
    </svg>
  );
}

const INPUT_BASE_CLASSES =
  'h-14 w-full rounded-xl border border-input-border-gray border-2 bg-gray-50 px-3 py-[10px] ' +
  'focus-within:border-button-primary focus-within:outline-none';

export function DatePicker({
  value,
  onChange,
  onOpen,
  InputProps,
  components,
  PopperProps,
  inputClassName,
  ...props
}: DatePickerProps) {
  const [datePickerWrapperEl, setDatePickerWrapperEl] = useState<HTMLDivElement | null>(null);

  return (
    <div ref={setDatePickerWrapperEl}>
      <DesktopDatePicker
        {...props}
        disableFuture={true}
        inputFormat="MM/DD/YYYY"
        PopperProps={{
          container: datePickerWrapperEl,
          ...PopperProps,
        }}
        value={value}
        onChange={onChange}
        onOpen={onOpen}
        components={{
          OpenPickerIcon: CalendarIcon,
          ...components,
        }}
        InputProps={{
          className: inputClassName ? `${INPUT_BASE_CLASSES} ${inputClassName}` : INPUT_BASE_CLASSES,
          ...InputProps,
          disableUnderline: true,
        }}
        renderInput={(params) => (
          <TextField
            id="date_of_birth"
            {...params}
            variant="standard"
            sx={{
              width: '100%',
              svg: { color: '#101828' },
              '.MuiInputBase-input': {
                padding: 0,
                background: 'transparent',
                fontSize: '1rem',
                lineHeight: 1.5,
                '&::placeholder': {
                  color: '#9ca3af',
                  opacity: 1,
                },
                ':focus': {
                  boxShadow: 'none',
                  outline: 'none',
                },
              },
            }}
          />
        )}
      />
    </div>
  );
}
